import { useState } from 'react';
import {
  Box,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  IconButton,
  Tooltip,
  Button,
  Flex,
  Input,
  Select,
  Text,
} from '@chakra-ui/react';
import { MdTune } from 'react-icons/md';

import { GridColumn, GridView, GridViewStore, defaultView } from './model';
import { ViewFields } from './view-fields';

type Props<T> = {
  columns: GridColumn<T>[];
  view: GridView;
  onChange: (view: GridView) => void;
  storageKey: string;
  store: GridViewStore;
  onStore: (store: GridViewStore) => void;
  disabled: boolean;
};

export function ViewSettings<T>({
  columns,
  view,
  onChange,
  storageKey,
  store,
  onStore,
  disabled,
}: Props<T>) {
  const [active, setActive] = useState(store.defaultId || '');
  const [name, setName] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState('');
  const persist = (next: GridViewStore) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
      onStore(next);
      setError('');
      return true;
    } catch {
      setError('Не удалось сохранить настройки в браузере.');
      return false;
    }
  };
  const saved = store.views.find((v) => v.id === active);
  const changed = saved && JSON.stringify(saved.view) !== JSON.stringify(view);
  return (
    <Box>
      <Flex gap={2} wrap="wrap" align="center">
        <Select
          aria-label="Представление"
          w={{ base: '190px', md: '230px' }}
          size="sm"
          value={active}
          isDisabled={disabled}
          onChange={(e) => {
            setActive(e.target.value);
            onChange(
              store.views.find((v) => v.id === e.target.value)?.view ||
                defaultView(columns)
            );
          }}
        >
          <option value="">Исходное представление</option>
          {store.views.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
              {v.id === store.defaultId ? ' ★' : ''}
            </option>
          ))}
        </Select>
        {changed && (
          <Text fontSize="sm" color="orange.700">
            Настройки изменены
          </Text>
        )}
        <Tooltip label="Настроить представление">
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Настроить представление"
            icon={<MdTune size={19} />}
            onClick={() => setExpanded(true)}
            isDisabled={disabled}
          />
        </Tooltip>
      </Flex>
      <Drawer
        isOpen={expanded}
        onClose={() => setExpanded(false)}
        placement="right"
        size="xl"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Настройка представления</DrawerHeader>
          <DrawerBody pb={6}>
            <Text fontSize="sm" mb={2}>
              Представления сохраняются только в этом браузере для вашего
              пользователя.
            </Text>
            <Flex gap={2} wrap="wrap" mb={3}>
              <Input
                size="sm"
                w="230px"
                placeholder="Название представления"
                aria-label="Название представления"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Button
                size="sm"
                isDisabled={disabled || !name.trim()}
                onClick={() => {
                  const id = `${Date.now()}-${Math.random()
                    .toString(36)
                    .slice(2)}`;
                  if (
                    persist({
                      ...store,
                      views: [...store.views, { id, name: name.trim(), view }],
                    })
                  ) {
                    setActive(id);
                    setName('');
                  }
                }}
              >
                Сохранить как новое
              </Button>
              <Button
                size="sm"
                isDisabled={disabled || !saved}
                onClick={() =>
                  persist({
                    ...store,
                    views: store.views.map((v) =>
                      v.id === active
                        ? { ...v, view, name: name.trim() || v.name }
                        : v
                    ),
                  })
                }
              >
                Обновить выбранное
              </Button>
              <Button
                size="sm"
                isDisabled={disabled || !saved}
                onClick={() =>
                  persist({
                    ...store,
                    defaultId: store.defaultId === active ? undefined : active,
                  })
                }
              >
                {store.defaultId === active && active
                  ? 'Убрать по умолчанию'
                  : 'По умолчанию'}
              </Button>
              <Button
                size="sm"
                isDisabled={disabled || !saved}
                onClick={() => {
                  if (
                    persist({
                      ...store,
                      defaultId:
                        store.defaultId === active
                          ? undefined
                          : store.defaultId,
                      views: store.views.filter((v) => v.id !== active),
                    })
                  ) {
                    setActive('');
                  }
                }}
              >
                Удалить представление
              </Button>
              <Button
                size="sm"
                isDisabled={disabled}
                onClick={() => {
                  onChange(defaultView(columns));
                  setActive('');
                }}
              >
                Сбросить настройки
              </Button>
            </Flex>
            <ViewFields
              columns={columns}
              view={view}
              onChange={onChange}
              disabled={disabled}
            />
            {error && (
              <Text role="alert" color="red.600">
                {error}
              </Text>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}
